import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useEffect, useRef } from "react";
import { getCSS, getCSSVariableValue } from "../../../assets/ts/_utils";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";

type Props = {
  className: string;
  label: string[];
  dataList: number[];
  title: string;
  eyebrow?: string;
  description?: string;
};

const ChartsWidget: React.FC<Props> = ({
  className,
  label,
  dataList,
  title,
  eyebrow,
  description,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();
  const hasData = dataList.some((value) => value > 0);

  useEffect(() => {
    if (!chartRef.current || !label.length || !dataList.length || !hasData) {
      return;
    }

    const height = parseInt(getCSS(chartRef.current, "height"));
    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, label, dataList),
    );
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartRef, mode, label, dataList, hasData]);

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div className="card-header border-0">
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{title}</h3>
          <p className="dashboard-chart-card__description">
            {description ?? "A detailed breakdown of event distribution."}
          </p>
        </div>
      </div>
      <div className="card-body dashboard-chart-card__body">
        {!hasData ? (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">EC</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No category activity yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                Category insights will appear once events start getting grouped.
              </span>
            </span>
          </div>
        ) : null}
        <div
          ref={chartRef}
          className="dashboard-chart-canvas"
          style={{ height: "350px" }}
        ></div>
      </div>
    </div>
  );
};

export { ChartsWidget };

function getChartOptions(
  height: number,
  label: string[],
  dataList: number[],
): ApexOptions {
  const labelColor = getCSSVariableValue("--bs-gray-600");
  const borderColor = "#E2E8F0";

  return {
    series: [
      {
        name: "Count",
        data: dataList,
      },
    ],
    chart: {
      fontFamily: "inherit",
      type: "bar",
      height: height,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 80,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "46%",
        borderRadius: 12,
        dataLabels: {
          position: "top",
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val > 0 ? val.toString() : "";
      },
      offsetY: -14,
      style: {
        fontSize: "12px",
        fontWeight: 800,
        colors: ["#0F172A"],
      },
      background: {
        enabled: false,
        dropShadow: {
          enabled: false,
        },
      },
      dropShadow: {
        enabled: false,
      },
    },
    stroke: {
      show: true,
      width: 0,
      colors: ["transparent"],
    },
    xaxis: {
      categories: label,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: "12px",
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#94A3B8",
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.18,
        gradientToColors: ["#F59E0B"],
        inverseColors: false,
        opacityFrom: 0.97,
        opacityTo: 0.82,
        stops: [0, 100],
      },
    },
    states: {
      normal: {
        filter: {
          type: "none",
          value: 0,
        },
      },
      hover: {
        filter: {
          type: "lighten",
          value: 0.04,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: "lighten",
          value: 0.02,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
      },
      y: {
        formatter: function (val) {
          return "" + val + " Event(s)";
        },
      },
    },
    colors: ["#EAB308"],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 5,
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        top: 18,
        right: 8,
        bottom: 0,
        left: 8,
      },
    },
  };
}
